// /* @@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@ */
// /* @@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@ THIS HOLDS THE PRECOLLEGE COURSE FILTER @@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@ */
// /* @@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@ */

import React, { useState, useEffect } from "react";
import Seo from "../components/seo";
import { graphql, useStaticQuery } from "gatsby";
import { Container, Row, Col } from "react-bootstrap";
import "../styles/index-styles.css";
import "../styles/course-finder-styles.css";
import { renderRichText } from "gatsby-source-contentful/rich-text";
import { Button } from "@contentful/f36-components";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCircleExclamation,
  faCircleInfo,
  faFilter,
} from "@fortawesome/free-solid-svg-icons";

const PreCollegeCourseFilter = () => {
  const data = useStaticQuery(graphql`
    query {
      allContentfulSuCourse {
        nodes {
          courseTitle
          courseImage {
            url
          }
          courseDescription {
            raw
          }
          courseType
          courseSlug
          contentful_id
          id
          startOfSession
          remoteOrCampus
          courseStartDate
          courseEndDate
          classDays
          classStartTime
          classEndTime
          academicInterest
          duration
          currentGrade
          courseCode
          coreCourse
          courseFull
        }
      }
    }
  `);

  const courses = data.allContentfulSuCourse.nodes;

  // @@@@@@@@@@@@@@@@@@@@@@@@@@@@ Get unique values from the data for each filter category @@@@@@@@@@@@@@@@@@@@@@@@@@@@
  const uniqueValues = (key) =>
    [...new Set(courses.map((course) => course[key]))].filter(Boolean);

  const sortedDurationOptions = uniqueValues("duration").sort((a, b) => {
    const numA = parseInt(a, 10);
    const numB = parseInt(b, 10);
    if (!isNaN(numA) && !isNaN(numB)) return numA - numB;
    if (!isNaN(numA)) return -1;
    if (!isNaN(numB)) return 1;
    return a.localeCompare(b);
  });

  const gradeOptions = uniqueValues("currentGrade");
  const timeframeOptions = uniqueValues("startOfSession");
  const interestOptions = uniqueValues("academicInterest");
  const formatOptions = uniqueValues("remoteOrCampus");

  // @@@@@@@@@@@@@@@@@@@@@@@@@@@@ State for selected filters @@@@@@@@@@@@@@@@@@@@@@@@@@@@

  const [searchQuery, setSearchQuery] = useState("");
  const [filteredSuggestions, setFilteredSuggestions] = useState([]);
  const [isDropdownVisible, setIsDropdownVisible] = useState(false);

  const [selectedDurations, setSelectedDurations] = useState([]);
  const [selectedGrade, setSelectedGrade] = useState("");
  const [selectedTimeframes, setSelectedTimeframes] = useState([]);
  const [selectedInterests, setSelectedInterests] = useState([]);
  const [selectedFormats, setSelectedFormats] = useState([]);
  const [isFilterActive, setIsFilterActive] = useState(false);

  // @@@@@@@@@@@@@@@@@@@@@@@@@@@@ Update suggestions based on search query @@@@@@@@@@@@@@@@@@@@@@@@@@@@
  useEffect(() => {
    if (searchQuery.trim() === "") {
      setFilteredSuggestions([]);
      setIsDropdownVisible(false);
    } else {
      const filtered = courses.filter(
        (course) =>
          course.courseTitle
            .toLowerCase()
            .includes(searchQuery.trim().toLowerCase()) &&
          (course.courseType === "Pre-College" || course.courseType === "Both")
      );
      setFilteredSuggestions(filtered);
      setIsDropdownVisible(filtered.length > 0);
    }
  }, [searchQuery, courses]);

  const handleInputChange = (e) => {
    setSearchQuery(e.target.value);
  };

  const handleSuggestionClick = (courseTitle) => {
    setSearchQuery(courseTitle);
    setFilteredSuggestions([]);
    setIsDropdownVisible(false);
  };

  // @@@@@@@@@@@@@@@@@@@@@@@@@@@@ Effect to check if any filter is selected @@@@@@@@@@@@@@@@@@@@@@@@@@@@
  useEffect(() => {
    const hasActiveFilter =
      selectedDurations.length > 0 ||
      selectedGrade !== "" ||
      selectedTimeframes.length > 0 ||
      selectedInterests.length > 0 ||
      selectedFormats.length > 0;

    setIsFilterActive(hasActiveFilter);
  }, [
    selectedDurations,
    selectedGrade,
    selectedTimeframes,
    selectedInterests,
    selectedFormats,
  ]);

  // @@@@@@@@@@@@@@@@@@@@@@@@@@@@ Handle checkbox changes for each filter category @@@@@@@@@@@@@@@@@@@@@@@@@@@@
  const handleCheckboxChange = (event, setSelectedFunction, selectedValues) => {
    const { value, checked } = event.target;
    if (checked) {
      setSelectedFunction([...selectedValues, value]);
    } else {
      setSelectedFunction(selectedValues.filter((item) => item !== value));
    }
  };

  // @@@@@@@@@@@@@@@@@@@@@@@@@@@@ Reset function to clear all filters @@@@@@@@@@@@@@@@@@@@@@@@@@@@
  const resetFilters = () => {
    setSelectedDurations([]);
    setSelectedGrade(""); // Reset to empty string
    setSelectedTimeframes([]);
    setSelectedInterests([]);
    setSelectedFormats([]);
    setSearchQuery("");

    const allInputs = document.querySelectorAll("input");
    allInputs.forEach((input) => {
      input.checked = false;
      input.blur();
    });
  };

  // @@@@@@@@@@@@@@@@@@@@@@@@@@@@ Filter the courses based on selected filters @@@@@@@@@@@@@@@@@@@@@@@@@@@@
  const filteredPrograms = courses.filter((program) => {
    const escapeRegExp = (string) => {
      return string.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"); // Escape special characters
    };

    const matchesSearchQuery =
      searchQuery.trim() === "" ||
      new RegExp(`\\b${escapeRegExp(searchQuery.trim())}\\b`, "i").test(
        program.courseTitle
      );

    const matchesCourseType =
      program.courseType === "Pre-College" || program.courseType === "Both";

    const matchesDuration =
      selectedDurations.length === 0 ||
      selectedDurations.includes(program.duration);

    // @@@@@@@@@@@@@@@@@@@@@@@@@@@@ Updated grade level matching logic for exact matches @@@@@@@@@@@@@@@@@@@@@@@@@@@@
    const matchesGrade =
      selectedGrade === "" || // If no grade is selected, include all courses
      (Array.isArray(program.currentGrade)
        ? program.currentGrade.includes(selectedGrade)
        : program.currentGrade === selectedGrade);

    const matchesTimeframe =
      selectedTimeframes.length === 0 ||
      selectedTimeframes.includes(program.startOfSession);

    const matchesInterest =
      selectedInterests.length === 0 ||
      selectedInterests.some((selectedInterest) =>
        Array.isArray(program.academicInterest)
          ? program.academicInterest.includes(selectedInterest)
          : program.academicInterest === selectedInterest
      );

    const matchesFormat =
      selectedFormats.length === 0 ||
      selectedFormats.includes(program.remoteOrCampus);

    const doesNotStartWithZZZ = !program.courseTitle.startsWith(
      "ZZZ Placeholder"
    );

    return (
      matchesSearchQuery &&
      matchesCourseType &&
      matchesDuration &&
      matchesGrade &&
      matchesTimeframe &&
      matchesInterest &&
      matchesFormat &&
      doesNotStartWithZZZ
    );
  });

  // @@@@@@@@@@@@@@@@@@@@@@@@@@@@ ALPHABETICALLY SORTED PROGRAM RESULTS @@@@@@@@@@@@@@@@@@@@@@@@@@@@
  const sortedPrograms = filteredPrograms
    .sort((a, b) => a.courseTitle.localeCompare(b.courseTitle))
    .filter((program) =>
      program.courseTitle.toLowerCase().includes(searchQuery.toLowerCase())
    );

  // // Sort the filtered programs
  // const sortedPrograms = filteredPrograms.sort((a, b) => {
  //   const durationA = a.duration || "";
  //   const durationB = b.duration || "";

  //   const numA = parseInt(durationA, 10);
  //   const numB = parseInt(durationB, 10);

  //   if (!isNaN(numA) && !isNaN(numB)) return numA - numB;
  //   if (!isNaN(numA)) return -1;
  //   if (!isNaN(numB)) return 1;

  //   return durationA.localeCompare(durationB, undefined, { numeric: true });
  // });

  // Function to handle "Click All" for Interest
  // const handleClickAllInterests = (event) => {
  //   const { checked } = event.target;
  //   setSelectedInterests(checked ? interestOptions : []);
  // };


  const handleClickAllInterests = (event) => {
    const { checked } = event.target;
    setSelectedInterests(
      checked
        ? [
          ...new Set(
            interestOptions.flatMap((interest) =>
              Array.isArray(interest) ? interest : [interest]
            )
          ),
        ].filter(Boolean)
        : []
    );
  };


  // @@@@@@@@@@@@@@@@@@@@@@@@@@@@ Function to handle "Click All" for Time Frame/Dates @@@@@@@@@@@@@@@@@@@@@@@@@@@@
  const handleClickAllTimeframes = (event) => {
    const { checked } = event.target;
    setSelectedTimeframes(checked ? timeframeOptions : []);
  };

  // @@@@@@@@@@@@@@@@@@@@@@@@@@@@ Function to handle "Click All" for Program/Duration @@@@@@@@@@@@@@@@@@@@@@@@@@@@
  const handleClickAllPrograms = (event) => {
    const { checked } = event.target;
    setSelectedDurations(checked ? sortedDurationOptions : []);
  };

  const separatePrograms = sortedPrograms.reduce(
    (acc, program) => {
      if (
        program.courseType === "Pre-College" &&
        program.courseFull === "Course FULL"
      ) {
        acc.fullCourses.push(program);
      } else {
        acc.availableCourses.push(program);
      }
      return acc;
    },
    { availableCourses: [], fullCourses: [] }
  );

  return (
    <Container className="pre-college-filter-box">
      <div key={courses.id} id={courses.styleId}>



        <button class="button" onClick={resetFilters}>
          <svg class="svgIcon" viewBox="0 0 384 512">
            <FontAwesomeIcon icon={faFilter} />
          </svg>
        </button>


        {/* @@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@ Grade Level Filter @@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@ */}
        <Container>
          <Row className="filter-container">
            <Col className="filter-column" xs={{ span: 6, order: 1 }} md={{ span: 4, order: 1 }}>
              {/* Grade/Education Level Filter */}
              <div key={courses.id} id={courses.styleId}>
                <h4>Current Grade / Education Level</h4>
                {[
                  ...new Set(
                    gradeOptions.flatMap((grade) =>
                      Array.isArray(grade) ? grade : [grade]
                    )
                  ),
                ]
                  .sort((a, b) =>
                    a.localeCompare(b, undefined, { numeric: true })
                  )
                  .map((currentGrade, index) => (
                    <div key={`${currentGrade}-${index}`}>
                      <div className="label-container">
                        <input
                          type="checkbox"
                          id={currentGrade}
                          name="currentGrade"
                          value={currentGrade}
                          checked={selectedGrade === currentGrade}
                          onChange={(e) => setSelectedGrade(e.target.value)}
                        />
                        <label htmlFor={currentGrade}>
                          {currentGrade}
                          {currentGrade.includes("12th Grade") && (
                            <>
                              <div className="twelfth-wrapper">
                                <span>
                                  <FontAwesomeIcon icon={faCircleExclamation} />
                                </span>
                                <div className="hover-message-filter">
                                  Students admitted to the College at UChicago
                                  have a different registration process and should{" "}
                                  <u>not</u> apply as a pre-college student.
                                </div>
                              </div>
                            </>
                          )}
                        </label>
                      </div>
                    </div>
                  ))}
              </div>

              {/* @@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@ Modality Filter @@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@ */}
              <div key={courses.id} id={courses.styleId}>
                <h4>Modality</h4>
                {formatOptions
                  .filter((option) => option !== "Hybrid")
                  .map((remoteOrCampus) => (
                    <div key={remoteOrCampus}>
                      <div class="label-container">
                        <input
                          type="checkbox"
                          id={remoteOrCampus}
                          value={remoteOrCampus}
                          checked={selectedFormats.includes(remoteOrCampus)}
                          onChange={(e) =>
                            handleCheckboxChange(
                              e,
                              setSelectedFormats,
                              selectedFormats
                            )
                          }
                        />

                        <label htmlFor={remoteOrCampus}>{remoteOrCampus}</label>
                      </div>
                    </div>
                  ))}
              </div>
            </Col>

            <Col className="filter-column areas-interest" xs={{ span: 12, order: 3 }} md={{ span: 4, order: 2 }}>

              {/* @@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@ Area of Interest Filter @@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@ */}
              <h4>Area(s) of Interest</h4>
              <div>

                <div>
                  <div className="label-container">
                    <input
                      type="checkbox"
                      id="clickAllAreaofInterest"
                      onChange={handleClickAllInterests}
                      checked={
                        selectedInterests.length ===
                        [
                          ...new Set(
                            interestOptions.flatMap((interest) =>
                              Array.isArray(interest) ? interest : [interest]
                            )
                          ),
                        ].filter(Boolean).length
                      }
                    />
                    <label htmlFor="clickAllAreaofInterest">Select All</label>
                  </div>
                </div>
                {[
                  ...new Set(
                    interestOptions.flatMap((interest) =>
                      Array.isArray(interest) ? interest : [interest]
                    )
                  ),
                ]
                  .filter(Boolean)
                  .sort((a, b) => a.localeCompare(b))
                  .map((academicInterest, index) => (
                    <div key={`${academicInterest}-${index}`}>
                      <div className="label-container">
                        <input
                          type="checkbox"
                          id={academicInterest}
                          value={academicInterest}
                          checked={selectedInterests.includes(academicInterest)}
                          onChange={(e) =>
                            handleCheckboxChange(
                              e,
                              setSelectedInterests,
                              selectedInterests
                            )
                          }
                        />
                        <label htmlFor={academicInterest}>
                          {academicInterest}
                        </label>
                        <span></span>
                      </div>
                    </div>
                  ))}
              </div>
            </Col>
            <Col className="filter-column" xs={{ span: 6, order: 2 }} md={{ span: 4, order: 3 }} >

              {/* @@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@ Session Filter @@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@ */}
              <div>
                <h4>Session</h4>
                <div>
                  <div className="label-container">
                    <input
                      type="checkbox"
                      id="clickAllTimeframes"
                      onChange={handleClickAllTimeframes}
                      checked={
                        selectedTimeframes.length === timeframeOptions.length
                      }
                    />
                    <label htmlFor="clickAllTimeframes">Select All</label>
                  </div>
                </div>
                {timeframeOptions
                  .filter((startOfSession) => startOfSession !== "September Term") // Exclude "September Term"
                  .sort((a, b) => a.localeCompare(b))
                  .map((startOfSession) => (
                    <div key={startOfSession}>
                      <div className="label-container">
                        <input
                          type="checkbox"
                          id={startOfSession}
                          value={startOfSession}
                          checked={selectedTimeframes.includes(startOfSession)}
                          onChange={(e) =>
                            handleCheckboxChange(
                              e,
                              setSelectedTimeframes,
                              selectedTimeframes
                            )
                          }
                        />
                        <label htmlFor={startOfSession}>{startOfSession}</label>
                      </div>
                    </div>
                  ))}
              </div>

              {/* @@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@ Program Filter @@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@ */}
              <div>
                <h4>Program</h4>
                <div class="label-container">
                  <input
                    type="checkbox"
                    id="clickAllPrograms"
                    onChange={handleClickAllPrograms}
                    checked={
                      selectedDurations.length === sortedDurationOptions.length
                    }
                  />
                  <label htmlFor="clickAllPrograms">Select All</label>
                </div>
                {sortedDurationOptions.map((duration) => (
                  <div key={duration}>
                    <div class="label-container">
                      <input
                        type="checkbox"
                        id={duration}
                        value={duration}
                        checked={selectedDurations.includes(duration)}
                        onChange={(e) =>
                          handleCheckboxChange(
                            e,
                            setSelectedDurations,
                            selectedDurations
                          )
                        }
                      />
                      <label htmlFor={duration}>{duration}</label>
                    </div>
                  </div>
                ))}
              </div>
            </Col>
          </Row>
        </Container>

        {/* @@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@ Results Area @@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@ */}
        {isFilterActive && (
          <div>
            {/* @@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@ SEARCH BAR - COURSE TITLE @@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@ */}
            <Container>
              <Row className="align-items-center">
                <Col>
                  <p className="class-filter-area-title">
                    Results based on your search criteria
                  </p>
                </Col>

                {/* <Col>
                  <div className="search-bar-container">
                    <input
                      type="text"
                      className="search-bar"
                      placeholder="Search for a course by name..."
                      value={searchQuery}
                      onChange={handleInputChange}
                    />
                    {isDropdownVisible && (
                      <ul className="suggestions-dropdown">
                        {filteredSuggestions.map((course) => (
                          <li
                            key={course.id}
                            onClick={() => handleSuggestionClick(course.courseTitle)}
                          >
                            {course.courseTitle}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </Col> */}
              </Row>
            </Container>

            <Container>
              {sortedPrograms.length > 0 ? (
                <>
                  {/* @@@@@@@@@@@@@@@@@@@@@@@@@@@@ Available Courses Section @@@@@@@@@@@@@@@@@@@@@@@@@@@@ */}
                  {separatePrograms.availableCourses.length > 0 && (
                    <ul className="filtered-programs-list justify-content-center">
                      {separatePrograms.availableCourses.map((program) => (
                        <li key={program.id}>

                          <a
                            href={`/courses/${program.courseSlug}`}
                            className="similar-course-link"
                          >

                            {program.courseImage && program.courseImage.url && (
                              <div>
                                <img
                                  src={program.courseImage.url}
                                  alt={program.courseTitle}
                                />
                              </div>
                            )}

                            <div className="course-title">
                              {program.courseTitle}
                            </div>
                          </a>

                          <div className="course-description">
                            {program.courseDescription ? (
                              <>
                                <p className="description-text">
                                  {renderRichText(program.courseDescription)}
                                </p>
                                <a
                                  href={`/courses/${program.courseSlug}`}
                                  className="view-course-link"
                                >
                                  View Course
                                </a>
                              </>
                            ) : null}
                          </div>

                          <div className="course-container">
                            <div
                              className={
                                program.remoteOrCampus === "Remote"
                                  ? "remote-campus"
                                  : program.remoteOrCampus === "Residential"
                                    ? "residential-campus"
                                    : program.remoteOrCampus === "Hybrid"
                                      ? "hybrid-campus"
                                      : ""
                              }
                            >
                              {program.remoteOrCampus}
                            </div>
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}

                  {/* @@@@@@@@@@@@@@@@@@@@@@@@@@@@ FULL Courses Section @@@@@@@@@@@@@@@@@@@@@@@@@@@@ */}
                  {separatePrograms.fullCourses.length > 0 && (
                    <div className="full-courses-section">
                      <h5 className="full-courses-title">Course(s) that have reached full capacity:
                      </h5>
                      <h5 className="full-courses-subtitle">Missed out on a course? No problem—check out our other exciting options!
                      </h5>
                      <ul className="filtered-programs-list justify-content-center full-courses">
                        {separatePrograms.fullCourses.map((program) => (
                          <li key={program.id} className="full-course-item">

                            <a
                              href={`/courses/${program.courseSlug}`}
                              className="similar-course-link"
                            >
                              {program.courseImage && program.courseImage.url && (
                                <div className="greyed-out-image">
                                  <img
                                    src={program.courseImage.url}
                                    alt={program.courseTitle}
                                  />
                                </div>
                              )}
                              <div className="course-title-full">
                                {program.courseTitle}
                              </div>
                            </a>

                            <div className="course-description">
                              {program.courseDescription ? (
                                <>
                                  <p className="description-text">
                                    {renderRichText(program.courseDescription)}
                                  </p>
                                  <a
                                    href={`/courses/${program.courseSlug}`}
                                    className="view-course-link-full"
                                  >
                                    View Course
                                  </a>
                                </>
                              ) : null}
                            </div>

                            <div className="course-container">
                              <div
                                className={
                                  program.remoteOrCampus === "Remote"
                                    ? "remote-campus"
                                    : program.remoteOrCampus === "Residential"
                                      ? "residential-campus"
                                      : program.remoteOrCampus === "Hybrid"
                                        ? "hybrid-campus"
                                        : ""
                                }
                              >
                                {program.remoteOrCampus}
                              </div>
                              <p className="course-full-cardtext">Course Full</p>
                            </div>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </>
              ) : (
                <h3>
                  No courses match your criteria at this time. Please try another search.
                </h3>
              )}
            </Container>
          </div>
        )}
      </div>
    </Container>
  );
};

export default PreCollegeCourseFilter;

export const Head = () => <Seo title="Course Filter" />;



{/* @@@@@@@@@@@@@@@@@@@@@@@@@@@@@@ START OF FILTERING TEST AREA @@@@@@@@@@@@@@@@@@@@@@@@@@@@@@ */ }
{/* <Container className="filter-testing-box">
                        <div>
                          PROGRAM TYPE:{" "}
                          <ul>
                            <li>{program.courseType}</li>
                          </ul>
                        </div>

                        <div>
                          DURATION:{" "}
                          <ul>
                            <li>{program.duration}</li>
                          </ul>
                        </div>
                        <br />

                        <div>CURRENT GRADE LEVEL:</div>
                        <ul>
                          {Array.isArray(program.currentGrade) ? (
                            program.currentGrade.map((interest, index) => (
                              <li key={index}>{interest}</li>
                            ))
                          ) : (
                            <li>{program.currentGrade}</li>
                          )}
                        </ul>

                        <br />
                        <div>
                          TIME FRAME/DATES:{" "}
                          <ul>
                            <li>{program.startOfSession}</li>
                          </ul>
                        </div>
                        <br />

                        <div>AREAS OF INTEREST:</div>
                        <ul>
                          {Array.isArray(program.academicInterest) ? (
                            program.academicInterest.map((interest, index) => (
                              <li key={index}>{interest}</li>
                            ))
                          ) : (
                            <li>{program.academicInterest}</li>
                          )}
                        </ul>
                      </Container> */}
{/* @@@@@@@@@@@@@@@@@@@@@@@@@@@@@@ END OF FILTERING TEST AREA @@@@@@@@@@@@@@@@@@@@@@@@@@@@@@ */ }